import { sortListItemsByRules } from '@/shared/utils/listSorting';

import type { SafetyIssue, SafetyIssueStatus } from './types';

export type SafetyIssueListStatusFilter = 'ALL' | SafetyIssueStatus;

export interface SafetyIssuesListFilters {
  layoutId: string;
  locationId: string;
  search: string;
  status: SafetyIssueListStatusFilter;
  tradeId: string;
}

export type SafetyIssuesSortKey =
  | 'description'
  | 'status'
  | 'layout'
  | 'location'
  | 'trade'
  | 'created_by'
  | 'created_at';

export interface SafetyIssuesSortState {
  key: SafetyIssuesSortKey;
  direction: 'asc' | 'desc';
}

const STATUS_SORT_ORDER: Record<SafetyIssueStatus, number> = {
  OPEN: 0,
  RESOLVED: 1,
};

export const getSafetyIssueCreatorName = (issue: SafetyIssue) => {
  if (!issue.created_by) return '—';
  const fullName = [issue.created_by.first_name, issue.created_by.last_name]
    .filter(Boolean)
    .join(' ');
  return fullName || issue.created_by.email;
};

export const filterSafetyIssues = (
  issues: readonly SafetyIssue[],
  filters: SafetyIssuesListFilters,
  layoutNames: ReadonlyMap<number, string>
) => {
  const query = filters.search.trim().toLocaleLowerCase();

  return issues.filter((issue) => {
    if (filters.status !== 'ALL' && issue.status !== filters.status) return false;
    if (filters.layoutId !== 'ALL' && issue.layout !== Number(filters.layoutId)) return false;
    if (filters.locationId !== 'ALL' && issue.location !== Number(filters.locationId)) return false;
    if (filters.tradeId !== 'ALL' && issue.trade !== filters.tradeId) return false;
    if (!query) return true;

    const searchable = [
      issue.description,
      issue.trade_detail?.name,
      issue.location_detail?.name,
      issue.layout == null ? '' : layoutNames.get(issue.layout),
      getSafetyIssueCreatorName(issue),
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase();

    return searchable.includes(query);
  });
};

export const sortSafetyIssues = (
  issues: readonly SafetyIssue[],
  sort: readonly SafetyIssuesSortState[],
  layoutNames: ReadonlyMap<number, string>
) =>
  sortListItemsByRules(issues, sort, (issue, key) => {
    switch (key) {
      case 'description':
        return issue.description;
      case 'status':
        return STATUS_SORT_ORDER[issue.status];
      case 'layout':
        return issue.layout == null ? null : (layoutNames.get(issue.layout) ?? null);
      case 'location':
        return issue.location_detail?.name ?? null;
      case 'trade':
        return issue.trade_detail?.name ?? null;
      case 'created_by':
        return issue.created_by ? getSafetyIssueCreatorName(issue) : null;
      case 'created_at':
        return Date.parse(issue.created_at);
    }
  });
